import type { AnalyticsTab } from "@/components/pages/analytics/config/analyticsTabs";
import { getOffersKpiByStage } from "@/components/pages/analytics/front-desk/components/offers/mockData";
import type { OffersStage } from "@/components/pages/analytics/front-desk/components/offers/types";
import type { TeamSegment } from "@/components/pages/analytics/front-desk/components/team/types";
import type { AdminTabMetricColumnDef } from "../types/adminHotelMetricTable";

const OVERVIEW_COLUMNS: AdminTabMetricColumnDef[] = [
  {
    key: "serviceRevenue",
    headerKey: "analytics.overview.kpi.serviceRevenue",
    headerFallback: "Service revenue",
  },
  {
    key: "conversionOfOffers",
    headerKey: "analytics.overview.kpi.conversionOfOffers",
    headerFallback: "Conversion of offers",
  },
  {
    key: "avgGuestRating",
    headerKey: "analytics.overview.kpi.avgGuestRatingForStay",
    headerFallback: "Avg. Guest Rating for Stay",
  },
  {
    key: "ordersForStay",
    headerKey: "analytics.overview.kpi.ordersForStay",
    headerFallback: "Orders for stay",
  },
];

const REVENUE_COLUMNS: AdminTabMetricColumnDef[] = [
  {
    key: "totalRevenue",
    headerKey: "analytics.revenue.totalRevenue",
    headerFallback: "Total Revenue",
  },
  {
    key: "avgCheckPerReservation",
    headerKey: "analytics.revenue.avgCheckPerReservation",
    headerFallback: "Avg. check per reservation",
  },
];

const SERVICES_COLUMNS: AdminTabMetricColumnDef[] = [
  {
    key: "totalRevenue",
    headerKey: "analytics.revenue.totalRevenue",
    headerFallback: "Total Revenue",
  },
];

const REVIEWS_COLUMNS: AdminTabMetricColumnDef[] = [
  {
    key: "overallRating",
    headerKey: "analytics.reviews.kpi.overallRating",
    headerFallback: "Overall Rating",
  },
  {
    key: "positiveNeutral",
    headerKey: "analytics.reviews.kpi.positiveNeutral",
    headerFallback: "Positive/Neutral",
  },
  {
    key: "negative",
    headerKey: "analytics.reviews.kpi.negative",
    headerFallback: "Negative",
  },
  {
    key: "reviewsCount",
    headerKey: "analytics.reviews.kpi.reviewsCount",
    headerFallback: "Reviews Count",
  },
];

const OPERATIONS_COLUMNS: AdminTabMetricColumnDef[] = [
  {
    key: "totalRequests",
    headerKey: "analytics.operations.kpi.totalRequests",
    headerFallback: "Total requests",
  },
  {
    key: "avgProcessingTime",
    headerKey: "analytics.operations.kpi.avgProcessingTime",
    headerFallback: "Avg. processing time",
  },
  {
    key: "completedOnTime",
    headerKey: "analytics.operations.kpi.completedOnTime",
    headerFallback: "% completed on time",
  },
  {
    key: "complaints",
    headerKey: "analytics.operations.kpi.complaints",
    headerFallback: "Complaints",
  },
];

const TEAM_COLUMNS_TOP: AdminTabMetricColumnDef[] = [
  {
    key: "tasksCompleted",
    headerKey: "analytics.team.kpi.tasksCompleted",
    headerFallback: "Tasks completed",
  },
  {
    key: "tasksCancelled",
    headerKey: "analytics.team.kpi.tasksCancelled",
    headerFallback: "Tasks cancelled",
  },
  {
    key: "avgTimeOnTask",
    headerKey: "analytics.team.kpi.avgTimeOnTask",
    headerFallback: "Avg time on task",
  },
];

const TEAM_COLUMNS_BOTTOM: AdminTabMetricColumnDef[] = [
  {
    key: "delays",
    headerKey: "analytics.team.kpi.delays",
    headerFallback: "Delays",
  },
  {
    key: "daysOff",
    headerKey: "analytics.team.kpi.dayOff",
    headerFallback: "Day off",
  },
];

const OFFERS_KPI_TITLE_TO_COLUMN: Record<string, AdminTabMetricColumnDef> = {
  "Total Revenue": {
    key: "totalRevenue",
    headerKey: "analytics.offers.kpi.totalRevenue",
    headerFallback: "Total Revenue",
  },
  "Conversion rate": {
    key: "conversionRate",
    headerKey: "analytics.offers.kpi.conversionRate",
    headerFallback: "Conversion rate",
  },
  "Guests offered": {
    key: "guestsOffered",
    headerKey: "analytics.offers.kpi.guestsOffered",
    headerFallback: "Guests offered",
  },
  "Avg. Revenue": {
    key: "avgRevenue",
    headerKey: "analytics.offers.kpi.avgRevenue",
    headerFallback: "Avg. Revenue",
  },
  "Total guests contacted": {
    key: "totalGuestsContacted",
    headerKey: "analytics.offers.kpi.totalGuestsContacted",
    headerFallback: "Total guests contacted",
  },
  "Only Opened": {
    key: "onlyOpened",
    headerKey: "analytics.offers.kpi.onlyOpened",
    headerFallback: "Only Opened",
  },
  Skipped: {
    key: "skipped",
    headerKey: "analytics.offers.kpi.skipped",
    headerFallback: "Skipped",
  },
};

export function getOffersMetricColumnsFromMetrics(
  metrics: Array<{ title: string }>,
): AdminTabMetricColumnDef[] {
  return metrics
    .map((metric) => OFFERS_KPI_TITLE_TO_COLUMN[metric.title])
    .filter((column): column is AdminTabMetricColumnDef => column != null);
}

export type AdminTabMetricColumnsContext = {
  offersStage?: OffersStage;
  teamSegment?: TeamSegment;
};

export function getOffersMetricColumns(
  stage: OffersStage,
): AdminTabMetricColumnDef[] {
  return getOffersMetricColumnsFromMetrics(getOffersKpiByStage(stage));
}

export function getAdminTabMetricColumns(
  tab: AnalyticsTab,
  context: AdminTabMetricColumnsContext = {},
): AdminTabMetricColumnDef[] {
  switch (tab) {
    case "overview":
      return OVERVIEW_COLUMNS;
    case "revenue":
      return REVENUE_COLUMNS;
    case "services":
      return SERVICES_COLUMNS;
    case "offers":
      return getOffersMetricColumns(context.offersStage ?? "preCheckIn");
    case "reviews":
      return REVIEWS_COLUMNS;
    case "operations":
      return OPERATIONS_COLUMNS;
    case "team":
      if (context.teamSegment === "teams") {
        return TEAM_COLUMNS_TOP;
      }
      return [...TEAM_COLUMNS_TOP, ...TEAM_COLUMNS_BOTTOM];
    default:
      return [];
  }
}
